import React, { useMemo, forwardRef } from "react";
import { useTranslation } from "react-i18next";
import { FixDropdownInput } from "../fix-dropdown-input";
import type { FixDropdownOptionItem } from "../fix-dropdown/FixDropdownOption";
import { supportedLanguages } from "@/constants/languages";
import { useServices } from "@/services-context";
import { Gtag } from "@/services/gtag.ts";
import { Icon } from "@/elements-react/fix-icon";
import { CurrencyConverter } from "@/util/currency-converter.ts";
import { ItemButton } from "@/elements-react/menu/ItemButton.tsx";
import { useState } from "preact/hooks";
import { DialogMobile } from "@/elements-react/dialog-mobile";
import { FixDialogSelector } from "@/elements-react/fix-dialog-selector";
import { LayoutStyleTypes } from "@/util-react/layout.tsx";
import { PARAMETERS_LISTENER_NAME } from "@/services/parameters.ts";
import { COUNTRIES_LISTENER_NAME } from "@/services/countries.ts";
import { CUSTOMER_CHANNEL_LISTENER_NAME } from "@/services/customer-channel.ts";
import { CURRENCY_SERVICE_LISTENER_NAME } from "@/services/currency-service.ts";

interface Country {
  id: string;
  name: string;
  code: string;
}
interface Currency {
  id: string;
  code: string;
}

export const FixDropdownInputCountry = forwardRef<HTMLDivElement, {
    value?: string;
    name?: string;
    customError?: string;
    onChange?: (value: string, name?: string) => void;
    type?: LayoutStyleTypes.TYPE_LG | LayoutStyleTypes.TYPE_SM;
}>(({ value = "", name, customError = "", onChange, type = LayoutStyleTypes.TYPE_LG }, ref) => {
  const { t } = useTranslation();
  const { parameters, countries, customerChannel } = useServices([COUNTRIES_LISTENER_NAME, PARAMETERS_LISTENER_NAME, CUSTOMER_CHANNEL_LISTENER_NAME]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const currentCountry = parameters.getVisibleCountry();

  const options: FixDropdownOptionItem[] = useMemo(
    () =>
      ((countries.getChannelCountries() ?? []) as Country[])
        .map((country) => ({
          value: country.code,
          label: country.name,
          selected: country.code === currentCountry,
        }))
        .sort((a: FixDropdownOptionItem, b: FixDropdownOptionItem) => a.label.localeCompare(b.label)),
    [customerChannel.availableCountries, currentCountry],
  );

  const placeholder = t("nhuz8dp4nq7nrm9y.select-country", "Select your country");

  const showDialog = () => {
    setDialogOpen(true);
  };

  const hideDialog = () => {
    setDialogOpen(false);
  };

  const onCountryChange = (country: string) => {
    Gtag.pushEvent("country_change", country);
    parameters.changeParameter("country", country);
    parameters.applyParameters();
  };

  const dialog = (
    <DialogMobile label={t("fj452w70kp85aen5.country", "Country")} onClose={hideDialog}>
      <FixDialogSelector
        items={options}
        onSelect={(value) => {
          onCountryChange?.(value as string);
          hideDialog();
        }}
      />
    </DialogMobile>
  );

  const smView = (
    <div style={{display: 'contents'}}>
      <ItemButton title={t("fj452w70kp85aen5.country", "Country")} sub={currentCountry} schema={"line"} onClick={showDialog} />
      {dialogOpen && dialog}
    </div>
  );

  const lgView = (
      <div ref={ref}>
    <FixDropdownInput
      items={options}
      value={currentCountry}
      placeholder={placeholder}
      customError={customError}
      searchable={true}
      loading={!countries.allCountriesIsLoaded}
      onSelect={(item) => onCountryChange?.(item.value as string, name)}
    />
      </div>
  );

  return type === LayoutStyleTypes.TYPE_SM ? smView : lgView;
});

export const FixDropdownInputCurrency: React.FC<{
  name?: string;
  customError?: string;
  onChange?: (value: string, name?: string) => void;
  type?: LayoutStyleTypes.TYPE_LG | LayoutStyleTypes.TYPE_SM;
}> = ({ name, customError = "", onChange, type = LayoutStyleTypes.TYPE_LG }) => {
  const { t } = useTranslation();
  const { currency, parameters } = useServices([CURRENCY_SERVICE_LISTENER_NAME, PARAMETERS_LISTENER_NAME]);
  const currencyService = currency;
  const currentCurrency = parameters.getVisibleCurrency();
  const [dialogOpen, setDialogOpen] = useState(false);

  const currencyLabel = (code: string | null | undefined) => {
    if (!code) {
      return "";
    }

    const symbol = CurrencyConverter.getCurrencySymbol(code);
    return symbol === code ? code : `${code} ${symbol}`;
  };

  const options: FixDropdownOptionItem[] = useMemo(
    () =>
      ((currencyService.currencies ?? []) as Currency[])
        .map((currency) => ({
          value: currency.code,
          label: currencyLabel(currency.code),
          selected: currency.code === currentCurrency,
        }))
        .sort((a: FixDropdownOptionItem, b: FixDropdownOptionItem) => a.label.localeCompare(b.label)),
    [currentCurrency, currencyService.currencies],
  );

  const placeholder = t("26eiec2enyejp9pg.select-currency", "Currency");

  const showDialog = () => {
    setDialogOpen(true);
  };

  const hideDialog = () => {
    setDialogOpen(false);
  };

  const onCurrencyChange = (currency: string) => {
    Gtag.pushEvent("currency_change", currency);
    parameters.changeParameter("currency", currency);
    parameters.applyParameters();
  };

  const dialog = (
    <DialogMobile label={t("26eiec2enyejp9pg.select-currency", "Currency")} onClose={hideDialog}>
      <FixDialogSelector
        items={options}
        onSelect={(value) => {
          onCurrencyChange?.(value as string);
          hideDialog();
        }}
      />
    </DialogMobile>
  );

  const smView = (
    <div style={{display: 'contents'}}>
      <ItemButton title={t("26eiec2enyejp9pg.select-currency", "Currency")} sub={currentCurrency} schema={"line"} onClick={showDialog} />
      {dialogOpen && dialog}
    </div>
  );

  const lgView = (
    <FixDropdownInput
      items={options}
      value={currentCurrency}
      placeholder={placeholder}
      customError={customError}
      searchable={true}
      loading={!currencyService.allCurrenciesIsLoaded}
      onSelect={(item) => onCurrencyChange?.(item.value as string)}
    />
  );

  return type === LayoutStyleTypes.TYPE_SM ? smView : lgView;
};

export const FixDropdownInputLanguage: React.FC<{
  name?: string;
  customError?: string;
  onChange?: (value: string, name?: string) => void;
  type?: LayoutStyleTypes.TYPE_LG | LayoutStyleTypes.TYPE_SM;
}> = ({ name, customError = "", onChange, type = LayoutStyleTypes.TYPE_LG }) => {
  const { parameters } = useServices([PARAMETERS_LISTENER_NAME]);
  const { t } = useTranslation();
  const currentLanguage = parameters.getVisibleLanguage();
  const [dialogOpen, setDialogOpen] = useState(false);

  const options: FixDropdownOptionItem[] = useMemo(
    () =>
      supportedLanguages.map((item) => ({
        value: item.code,
        label: item.name,
        icon: <Icon width={20} height={20} library={item.iconLibrary} name={item.icon} />,
        selected: item.code === currentLanguage,
      })),
    [currentLanguage],
  );

  const onLanguageChange = (lang: string) => {
    Gtag.pushEvent("language_change", lang);

    parameters.changeParameter("language", lang);
    parameters.applyParameters();
  };

  const placeholder = t("2z5w2khfh21knzuu.select-language", "Select your language");

  const showDialog = () => {
    setDialogOpen(true);
  };

  const hideDialog = () => {
    setDialogOpen(false);
  };

  const dialog = (
    <DialogMobile label={t("8732y230959uqpy0.language", "Language")} onClose={hideDialog}>
      <FixDialogSelector
        items={options}
        onSelect={(value) => {
          onLanguageChange(value as string);
          hideDialog();
        }}
      />
    </DialogMobile>
  );

  const smView = (
    <div style={{display: 'contents'}}>
      <ItemButton title={t("8732y230959uqpy0.language", "Language")} sub={currentLanguage} schema={"line"} onClick={showDialog} />
      {dialogOpen && dialog}
    </div>
  );

  const lgView = (
    <FixDropdownInput
      items={options}
      value={currentLanguage}
      placeholder={placeholder}
      customError={customError}
      onSelect={(item) => onLanguageChange(item.value as string)}
    />
  );

  return type === LayoutStyleTypes.TYPE_SM ? smView : lgView;
};
